/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import classnames from 'classnames';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import { Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {
    const { HeaderTag, HeaderContainer, HeaderTopBarContainer, Divider, config } = props;
    const { siteSubtext, siteInfotext } = config;
    const {
        searchHeader,
        closeLabel,
        lookForLabel,
        switchProfileHeader,
        profilesAvailableHeader,
        switchProfileNote,
        siteName,
        switchProfileButtonLabel,
        searchButtonLabel
    } = props.resources;
    const [modal, setModal] = React.useState(false);
    const [modalSite, setModalSite] = React.useState(false);
    const toggle = () => setModal(!modal);
    const toggleSite = () => setModalSite(!modalSite);
    return (
        <>
            <Modal role='document' aria-labelledby='SearchModalTitle' className='cui modal-md' isOpen={modal} toggle={toggle}>
                <div className='modal-header'>
                    <h2 id='SearchModalTitle' tabIndex={0} className='modal-title'>
                        {searchHeader}
                    </h2>
                    <button className='btn-modal-close cui btn-md primary-text' onClick={toggle}>
                        {closeLabel}
                    </button>
                </div>

                <ModalBody className='search-modal-body'>
                    <div>
                        <label htmlFor='searchForm'>{lookForLabel}</label>
                        {_renderReactFragment(props.search)}
                        {props.preferredStore}
                    </div>
                </ModalBody>
            </Modal>
            <Modal role='document' aria-labelledby='profileSwitchTitle' className='cui modal-md' isOpen={modalSite} toggle={toggleSite}>
                <div className='modal-header'>
                    <h2 id='profileSwitchTitle' tabIndex={0} className='modal-title'>
                        {switchProfileHeader}
                    </h2>
                    <button className='btn-modal-close cui btn-md primary-text' onClick={toggleSite}>
                        {closeLabel}
                    </button>
                </div>

                <ModalBody style={{ padding: 0 }}>
                    <div className='search-modal-body switch-site'>
                        <h3>{profilesAvailableHeader}</h3>
                        {props.siteOptions}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <p>{switchProfileNote}</p>
                    {/* <div className='btn-box'>
                        <button className='msc-btn primary cui btn-md'>Switch to profile</button>
                    </div> */}
                </ModalFooter>
            </Modal>
            <Module {...HeaderTag}>
                <div className='top-bar'>{_renderAccountBlock(props, false)}</div>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className='header-left-col'>
                            {props.logo}
                            <div className='siteOption'>
                                <strong>{siteName}</strong>
                                <div className='site-option-desktop'>
                                    <span className='infoTxt'>
                                        {siteSubtext}
                                        <button role='button' aria-label={siteInfotext} data-tooltip={siteInfotext} className='info-ico'>
                                            <span className='iconInfo'></span>
                                        </button>
                                    </span>
                                    <button className='ms-site__btn' onClick={toggleSite}>
                                        {switchProfileButtonLabel}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className='site-option-mobile'>
                            <span className='infoTxt'>
                                {siteSubtext}
                                <span aria-label={siteInfotext} title={siteInfotext} className='info-ico'>
                                    <span className='iconInfo'></span>
                                </span>
                            </span>
                            <button className='ms-site__btn' onClick={toggleSite}>
                                {switchProfileButtonLabel}
                            </button>
                        </div>
                        <div className='header-right-col'>
                            {props.navIcon}
                            <div className='ms-search with-submit bx-show'>
                                <div className='ms-search__label'>
                                    <button className='ms-search__icon msc-btn' onClick={toggle}>
                                        {searchButtonLabel}
                                    </button>
                                </div>
                            </div>

                            {/* {_renderReactFragment(props.search)}
                            {props.preferredStore} */}

                            {/* {props.wishListIconDesktop} */}
                            <Node {...Divider} />
                            {props.cartIcon}
                        </div>
                    </Node>
                    {_renderCollapseMenu(props)}
                    <Node className={'ms-header__desktop-view'}>{_renderReactFragment(props.menuBar)}</Node>
                </Node>
            </Module>
        </>
    );
};

function _renderCollapseMenu(props: IHeaderViewProps & IHeaderProps<{}>): JSX.Element | null {
    const {
        Divider,
        MobileMenuLinksContainer,
        mobileMenuCollapsed,
        resources: { navMenuHeader }
    } = props;
    return (
        <Modal
            role='document'
            aria-labelledby='navMenuTitle'
            className={'ms-header__collapsible-hamburger nav-modal'}
            isOpen={!mobileMenuCollapsed}
        >
            <div className='modal-header'>
                <h2 id='navMenuTitle' tabIndex={0} className='modal-title'>
                    {navMenuHeader}
                </h2>
                {props.navIcon}
            </div>
            <ModalBody>
                <Node {...MobileMenuLinksContainer}>
                    {_renderMobileAccountBlock(props, true)}
                    {/* {props.wishListIconMobile} */}
                </Node>
                <Node {...Divider} />
                {_renderReactFragment(props.menuBar)}
            </ModalBody>
        </Modal>
    );
}

const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return <>{customer ? customer.FirstName || customer.Name : ''}</>;
};

function _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const { AccountInfoDropdownParentContainer, signOutLink, signInLink, data, accountLinks } = props;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            'ms-header__drawer',
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );

        if (accountLinks) {
            return (
                <Drawer
                    className={accountClassName}
                    openGlyph='ms-header__drawer-open'
                    closeGlyph='ms-header__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={renderCustomerName(data.accountInformation)}
                >
                    <div>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }
    return null;
}

function _renderAccountBlock(props: IHeaderViewProps & IHeaderProps<{}>, renderForMobile: boolean): JSX.Element | null {
    const { AccountInfoDropdownParentContainer, AccountInfoDropdownPopoverConentContainer, signOutLink, signInLink, accountLinks } = props;
    const customer = props.data.accountInformation.result;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {/* VSI-Customization -- START Display username with Hello  */}
                    {_getAccountInfoDropdownButton(customer, props)}
                    {/* VSI-Customization -- END  */}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }

    return null;
}

// Display user name with Hello as per Design document.
function _getAccountInfoDropdownButton(
    customer: Customer | undefined,
    props: IHeaderViewProps & IHeaderProps<{}>
): React.ReactNode | undefined {
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const { helloLabel } = props.resources;
    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.MyProfile);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    const displayName = `${helloLabel} ${customer ? customer.FirstName || customer.Name : undefined}`;
    return (
        customer &&
        displayName && (
            <Button
                innerRef={props.AccountInfoDropdownPopoverConentContainer?.target!}
                className='ms-header__profile-button'
                aria-describedby='myprofilePopover'
                onClick={props.AccountInfoDropdownPopoverConentContainer?.toggle!}
                color='link'
                aria-label={displayName}
                aria-expanded={props.AccountInfoDropdownPopoverConentContainer?.isOpen!}
                {...attributes}
            >
                <span className='ms-profile-button-text'>{displayName}</span>
            </Button>
        )
    );
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </React.Fragment>
    );
}

export default headerView;
